class F4dStickyAtc {
  constructor(el) {
    this.el = el;
    this.sectionId = el.dataset.sectionId;
    this.position = el.dataset.position || 'bottom';

    this.mainContainer = document.querySelector('.custom-product-section');
    this.mainForm = document.getElementById('product-form');
    this.mainButton = this.mainContainer?.querySelector('.product-add-to-cart');
    this.mainQtyInput = document.getElementById('Quantity');

    if (!this.mainButton || !this.mainForm) return;

    this.variantEl = el.querySelector('[data-sticky-atc-variant]');
    this.priceEl = el.querySelector('[data-sticky-atc-price]');
    this.buttonEl = el.querySelector('[data-sticky-atc-button]');
    this.buttonTextEl = el.querySelector('[data-sticky-atc-button-text]');
    this.stickyQtyInput = el.querySelector('.f4d-sticky-atc__qty-input');
    this.purchaseOptionEl = el.querySelector('[data-sticky-atc-purchase-option]');

    const variantScript = document.getElementById(`product-variants-json-${this.sectionId}`);
    const moneyFormatInput = document.getElementById(`money-format-${this.sectionId}`);
    this.variants = variantScript ? JSON.parse(variantScript.textContent) : [];
    this.moneyFormat = moneyFormatInput ? moneyFormatInput.value : '';

    this.setupObserver();
    this.setupQuantitySync();
    this.setupVariantListener();
    this.setupPurchaseOptionSync();
  }

  setupObserver() {
    const observer = new IntersectionObserver(
      ([entry]) => {
        const visible = entry.isIntersecting;
        this.el.classList.toggle('is-visible', !visible);
        this.el.setAttribute('aria-hidden', visible ? 'true' : 'false');
        this.updateBackToTopOffset(!visible);
      },
      { threshold: 0 }
    );
    observer.observe(this.mainButton);
  }

  updateBackToTopOffset(stickyVisible) {
    if (this.position !== 'bottom') return;
    const offset = stickyVisible ? `${this.el.offsetHeight}px` : '0px';
    document.documentElement.style.setProperty('--sticky-atc-offset', offset);
  }

  setupQuantitySync() {
    if (!this.stickyQtyInput || !this.mainQtyInput) return;
    this.stickyQtyInput.addEventListener('change', () => {
      this.mainQtyInput.value = this.stickyQtyInput.value;
    });
    this.mainQtyInput.addEventListener('change', () => {
      this.stickyQtyInput.value = this.mainQtyInput.value;
    });
  }

  setupVariantListener() {
    if (!this.mainContainer) return;
    const radios = this.mainContainer.querySelectorAll('.product-variant-values input[type="radio"]');
    radios.forEach(radio => {
      radio.addEventListener('change', () => this.onVariantChange());
    });

    // Qty buttons in sticky bar
    this.el.querySelectorAll('.f4d-sticky-atc__qty-btn').forEach(btn => {
      btn.addEventListener('click', () => {
        if (!this.stickyQtyInput) return;
        let val = parseInt(this.stickyQtyInput.value) || 1;
        if (btn.dataset.action === 'increase') val++;
        else if (btn.dataset.action === 'decrease' && val > 1) val--;
        this.stickyQtyInput.value = val;
        if (this.mainQtyInput) this.mainQtyInput.value = val;
      });
    });
  }

  onVariantChange() {
    const fieldsets = Array.from(this.mainContainer.querySelectorAll('.product-variant-values'));
    const selectedOptions = fieldsets.map(fs => {
      const checked = fs.querySelector('input:checked');
      return checked ? checked.value : null;
    });
    const variant = this.variants.find(v =>
      selectedOptions.every((opt, i) => v.options[i] === opt)
    );

    if (!variant) {
      this.buttonEl?.setAttribute('disabled', '');
      if (this.buttonTextEl) this.buttonTextEl.textContent = this.buttonEl?.dataset.unavailableText || '';
      return;
    }

    if (this.variantEl) {
      this.variantEl.textContent = variant.title === 'Default Title' ? '' : variant.title;
    }
    if (this.priceEl) {
      this.priceEl.textContent = this.formatMoney(variant.price, this.moneyFormat);
    }

    if (variant.available) {
      this.buttonEl?.removeAttribute('disabled');
      if (this.buttonTextEl) this.buttonTextEl.textContent = this.buttonEl?.dataset.addText || '';
    } else {
      this.buttonEl?.setAttribute('disabled', '');
      if (this.buttonTextEl) this.buttonTextEl.textContent = this.buttonEl?.dataset.soldOutText || '';
    }

    this.syncPurchaseOptionFromDom();
  }

  setupPurchaseOptionSync() {
    if (!this.purchaseOptionEl) return;

    this.subscriptionContainer = document.querySelector('.selling_plan_app_container');

    this.boundHandleSellingPlanChanged = () => this.syncPurchaseOptionFromDom();
    document.addEventListener('firmhouse.sellingPlanChanged', this.boundHandleSellingPlanChanged);

    if (!this.subscriptionContainer) return;

    this.syncPurchaseOptionFromDom();

    this.purchaseOptionObserver = new MutationObserver(() => {
      clearTimeout(this.purchaseOptionSyncTimeout);
      this.purchaseOptionSyncTimeout = setTimeout(() => this.syncPurchaseOptionFromDom(), 150);
    });
    this.purchaseOptionObserver.observe(this.subscriptionContainer, {
      childList: true,
      subtree: true,
      characterData: true,
    });
  }

  syncPurchaseOptionFromDom() {
    const container = this.subscriptionContainer;
    if (!container || !this.purchaseOptionEl) return;

    const checkedRadio = container.querySelector('input[type="radio"][data-radio-type]:checked');

    if (!checkedRadio || checkedRadio.dataset.radioType !== 'selling_plan') {
      this.setPurchaseOption(false);
      return;
    }

    this.setPurchaseOption(true, checkedRadio);

    const totalPriceEl = checkedRadio.closest('.subscription-card')?.querySelector('.price-info .total-price');
    const totalPriceText = totalPriceEl?.textContent?.trim();
    if (totalPriceText && this.priceEl) {
      this.priceEl.textContent = totalPriceText;
    }
  }

  setPurchaseOption(isSubscription, contextEl) {
    if (!this.purchaseOptionEl) return;

    if (!isSubscription) {
      this.purchaseOptionEl.hidden = true;
      this.purchaseOptionEl.textContent = '';
      return;
    }

    const container = contextEl?.closest('.selling_plan_app_container') || document;
    const frequencySelect = container.querySelector('.frequency-select');
    const frequencyText = frequencySelect?.options[frequencySelect.selectedIndex]?.text?.trim();
    const subscriptionLabel = this.purchaseOptionEl.dataset.subscriptionLabel || '';

    this.purchaseOptionEl.textContent = frequencyText
      ? `${subscriptionLabel} · ${frequencyText}`
      : subscriptionLabel;
    this.purchaseOptionEl.hidden = false;
  }

  formatMoney(cents, format) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    const formatString = format || '€{{amount_with_comma_separator}}';
    function formatWithDelimiters(number, precision, thousands, decimal) {
      number = (number / 100.0).toFixed(precision);
      const parts = number.split('.');
      const euros = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      const cents = parts[1] ? (decimal + parts[1]) : '';
      return euros + cents;
    }
    const match = formatString.match(placeholderRegex);
    if (!match) return cents;
    let value = '';
    switch (match[1]) {
      case 'amount': value = formatWithDelimiters(cents, 2, ',', '.'); break;
      case 'amount_no_decimals': value = formatWithDelimiters(cents, 0, ',', ''); break;
      case 'amount_with_comma_separator': value = formatWithDelimiters(cents, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': value = formatWithDelimiters(cents, 0, '.', ''); break;
      default: value = formatWithDelimiters(cents, 2, '.', ',');
    }
    return formatString.replace(placeholderRegex, value);
  }
}

document.querySelectorAll('.f4d-sticky-atc').forEach(el => new F4dStickyAtc(el));
